<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col>
        <hm-alert />
        <p>To manage risk, you must first conduct an accurate and thorough assessment of the potential risks and vulnerabilities to the confidentiality, integrity, and availability of ePHI held by your practice (a.k.a. risk analysis). After the risk analysis you will create a risk management plan to document decisions regarding the implementation of security measures (a.k.a. safeguards).</p>
        <p>The end result of this process will be the creation of mitigation tasks (if any) to track the implementation of safeguards your organization has determined are necessary to reduce risk to reasonable and appropriate levels.</p>
        <hm-spinner v-if="!ready" />
      </b-col>
    </b-row>

    <b-row v-show="ready">
      <b-col>
        <b-button type="button" variant="primary" class="px-4" :to="nextStep.url">
          <span v-if="nextStep.id <= 1">Start New Risk Plan</span>
          <span v-else>Continue Risk Plan</span>
        </b-button>
      </b-col>
    </b-row>

    <b-row class="pt-4" v-show="ready">
      <b-col>
        <span class="mr-2">
          <i class="icon-compass"></i> - Risk Analysis Report
        </span>
        <span>
          <i class="icon-shield ml-2"></i> - Risk Management Report
        </span>
      </b-col>
    </b-row>

    <b-row class="pt-4" v-show="ready">
      <b-col>
        <b-table class="p-1" responsive bordered hover show-empty empty-text="No Risk Plans" :items="rmList" :fields="columns" :sort-by.sync="sortBy" :sort-desc="true">
          <template slot="cell(createdAt)" slot-scope="data">{{data.value|formatDate('YYYY-MM-DD')}}</template>
          <template slot="cell(name)" slot-scope="data">{{data.item.seed.practice.po.name}}</template>
          <template slot="cell(approvedDate)" slot-scope="data">{{data.value|formatDate('YYYY-MM-DD')}}</template>
          <template slot="cell(completedDate)" slot-scope="data">{{data.value|formatDate('YYYY-MM-DD')}}</template>
          <template slot="cell(reports)" slot-scope="data">
            <div>
              <b-button :id="'ra-btn-' + data.item.id" type="button" variant="primary" class="mr-2 mb-2" @click="onShowRA(data.item.id)">
                <i class="icon-compass"></i>
              </b-button>
              <b-button v-if="data.item.completed" :id="'rm-btn-' + data.item.id" type="button" variant="primary" class="mr-2 mb-2" @click="onShowRM(data.item.id)">
                <i class="icon-shield"></i>
              </b-button>
            </div>
          </template>
        </b-table>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { riskService } from '@/services'
import Enumerable from 'linq/linq.js'

export default {
  name: 'RiskList',
  data() {
    return {
      rmList: [],
      nextStep: { id: 0, url: '/risk/review' },
      sortBy: 'createdAt',
      columns: [{
        key: 'createdAt',
        label: 'Start Date',
        sortable: true
      }, {
        key: 'name',
        label: 'Completed By',
        sortable: true
      }, {
        key: 'approvedDate',
        label: 'Approved Date',
        sortable: true
      }, {
        key: 'completedDate',
        label: 'Completed Date',
        sortable: true
      }, {
        key: 'reports',
        label: 'Reports'
      }],
      ready: false
    }
  },
  computed: {
    ...mapState(['rmSteps'])
  },
  methods: {
    ...mapActions({
      alertException: 'alert/exception'
    }),
    onShowRA(id) {
      let routeData = this.$router.resolve({ name: 'Print.RiskAnalysisReport', params: { id: id } });
      window.open(routeData.href, '_blank');
    },
    onShowRM(id) {
      let routeData = this.$router.resolve({ name: 'Print.RiskPlanReport', params: { id: id } });
      window.open(routeData.href, '_blank');
    }
  },
  created() {
    this.ready = false;

    riskService.getRiskManagement()
      .then(data => {
        // determine if any risk plan is currently in progress
        let current = Enumerable.from(data)
          .where(x => { return (x.id.substring(0, 2) === '0:' && x.step); })
          .orderByDescending(x => { return x.id; })
          .firstOrDefault(null, { default: true, step: 0 }); // default to first step

        this.nextStep = Enumerable.from(this.rmSteps).where(x => { return x.id > current.step; }).orderBy(x => { return x.id; }).firstOrDefault(null, this.nextStep);
        console.log('Current risk step:', current.step, 'Go to step:', this.nextStep.id, 'URL:', this.nextStep.url);

        // show only completed rm plans
        this.rmList = Enumerable.from(data)
          .where(x => { return (x.id.substring(0, 2) === '1:'); })
          .toArray();
      })
      .catch(err => {
        this.alertException({ err, message: 'Error loading risk management data.' });
      }).finally(() => {
        this.ready = true;
      })
  }
}
</script>